import React, { useEffect, useState } from 'react';
import { Modal, Input, InputNumber, Select, Alert, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import QuizEditor from './QuizEditor';
import {
    MODULE_TYPE_LABELS,
    emptyQuizQuestion,
    moduleNeedsReview,
    validateQuizDraft,
    quizDraftToPayload,
    formatDdMmYyyy,
} from '../../utils/trainingModel';

const { Text } = Typography;

const TYPE_OPTIONS = Object.keys(MODULE_TYPE_LABELS).map((value) => ({ value, label: MODULE_TYPE_LABELS[value] }));

/**
 * Create a module, or edit one on a DRAFT course.
 *   POST  /projects/:id/training/courses/:courseId/modules
 *   PATCH /projects/:id/training/modules/:moduleId
 * Advisor / Lead Advisor only (server-enforced; the parent only mounts this for those personas).
 *
 * Editing is NOT approval (Commandment IV): an AI-drafted module stays "AI drafted — review"
 * after it is saved here; approval is a separate, explicit action on the module card
 * (ModuleCard "Review & approve"), and publishing waits on it.
 */
const ModuleFormModal = ({ open, projectId, courseId, module, onClose, onSaved }) => {
    const { request } = useApiRequest();
    const editing = !!module;
    const [moduleType, setModuleType] = useState('reading');
    const [title, setTitle] = useState('');
    const [duration, setDuration] = useState(null);
    const [readingText, setReadingText] = useState('');
    const [quiz, setQuiz] = useState({ questions: [emptyQuizQuestion()] });
    const [meetingId, setMeetingId] = useState(null);
    const [meetings, setMeetings] = useState([]);
    const [meetingsError, setMeetingsError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setModuleType(module?.moduleType || 'reading');
        setTitle(module?.title || '');
        setDuration(module?.durationMinutes ?? null);
        setReadingText(module?.moduleType === 'reading' ? module?.contentJson?.text || '' : '');
        setQuiz(
            module?.moduleType === 'quiz' && Array.isArray(module?.contentJson?.questions)
                ? { questions: module.contentJson.questions.map((q) => ({ prompt: q.prompt, options: [...q.options], correctIndex: q.correctIndex })) }
                : { questions: [emptyQuizQuestion()] },
        );
        setMeetingId(null);
        setError(null);
    }, [open, module]);

    useEffect(() => {
        if (!open || editing || moduleType !== 'live_session') return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await request('GET', '/meetings');
                // GET /meetings is already scoped to the caller server-side; this only narrows the
                // picker to the meetings that belong to THIS project (the API re-validates on save).
                if (!cancelled) setMeetings((data.meetings || []).filter((m) => m.projectId === projectId));
            } catch (err) {
                if (!cancelled) setMeetingsError(err.message || 'Could not load meetings.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, editing, moduleType, projectId, request]);

    const handleOk = async () => {
        if (!title.trim()) return setError('Title is required.');
        const body = { title: title.trim() };
        if (typeof duration === 'number') body.durationMinutes = duration;

        if (moduleType === 'reading') {
            if (!readingText.trim()) return setError('Reading modules need text.');
            if (!editing || readingText !== (module?.contentJson?.text || '')) body.contentJson = { text: readingText };
        } else if (moduleType === 'quiz') {
            const problem = validateQuizDraft(quiz);
            if (problem) return setError(problem);
            const payload = quizDraftToPayload(quiz);
            if (!editing || JSON.stringify(payload) !== JSON.stringify(module?.contentJson)) body.contentJson = payload;
        } else if (moduleType === 'live_session' && !editing) {
            if (!meetingId) return setError('Choose the meeting this live session is held in.');
            body.meetingId = meetingId;
        }
        if (!editing) body.moduleType = moduleType;

        setSaving(true);
        setError(null);
        try {
            const path = editing
                ? `/projects/${projectId}/training/modules/${module.id}`
                : `/projects/${projectId}/training/courses/${courseId}/modules`;
            const { data } = await request(editing ? 'PATCH' : 'POST', path, { body });
            message.success(editing ? 'Module saved.' : 'Module added.');
            onSaved(data.module);
        } catch (err) {
            setError(err.message || 'Could not save the module.');
        } finally {
            setSaving(false);
        }
    };

    const needsReview = editing && moduleNeedsReview(module);
    const contentEditable = moduleType === 'reading' || moduleType === 'quiz';
    const hasTranslations = editing && Array.isArray(module?.translations) && module.translations.length > 0;

    return (
        <Modal
            open={open}
            width={720}
            title={editing ? 'Edit module' : 'Add module'}
            okText={editing ? 'Save module' : 'Add module'}
            confirmLoading={saving}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {needsReview && (
                    <Alert
                        type="warning"
                        showIcon
                        message="AI drafted - saving your edits does not approve it"
                        description="Correct anything wrong and save, then use “Review & approve” on the module card once you are satisfied. The course cannot be published until every AI-drafted module is approved."
                    />
                )}
                {editing && module?.sourceType === 'ai_generated' && !needsReview && contentEditable && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Changing the content clears this module's approval"
                        description="Editing the text of an approved AI module withdraws the approval and resets its translations; approve it again afterwards. Title and duration changes do not."
                    />
                )}
                {editing && module?.reviewedAt && (
                    <Text type="secondary">Last reviewed {formatDdMmYyyy(module.reviewedAt)}.</Text>
                )}
                {hasTranslations && contentEditable && (
                    <Alert
                        type="info"
                        showIcon
                        message="Saving resets this module's translations to pending review"
                        description="Each language must be re-approved before learners can use it. If the text changed materially, re-run Translate so the translation matches."
                    />
                )}
                {!editing && (
                    <div>
                        <Text strong>Module type</Text>
                        <Select aria-label="Module type" style={{ width: '100%' }} value={moduleType} onChange={setModuleType} options={TYPE_OPTIONS} />
                    </div>
                )}
                <div>
                    <Text strong>Title</Text>
                    <Input aria-label="Module title" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div>
                    <Text strong>Duration (minutes)</Text>
                    <div>
                        <InputNumber aria-label="Duration in minutes" min={1} max={600} value={duration} onChange={setDuration} />
                    </div>
                </div>
                {moduleType === 'reading' && (
                    <div>
                        <Text strong>Reading text</Text>
                        <Input.TextArea aria-label="Reading text" rows={10} value={readingText} onChange={(e) => setReadingText(e.target.value)} />
                    </div>
                )}
                {moduleType === 'quiz' && (
                    <div>
                        <Text strong>Questions</Text>
                        <QuizEditor value={quiz} onChange={setQuiz} />
                    </div>
                )}
                {moduleType === 'video' && !editing && (
                    <Alert type="info" showIcon message="Add the module first, then upload the MP4 / WEBM / MOV file from the module card." />
                )}
                {moduleType === 'live_session' && !editing && (
                    <div>
                        <Text strong>Meeting</Text>
                        <Select
                            aria-label="Meeting"
                            style={{ width: '100%' }}
                            value={meetingId}
                            onChange={setMeetingId}
                            placeholder="Select a scheduled project meeting"
                            options={meetings.map((m) => ({
                                value: m.id,
                                label: `${m.title} - ${formatDdMmYyyy(m.meetingDate)}`,
                            }))}
                            notFoundContent={meetingsError || 'No meetings on this project. Schedule one in Meetings first.'}
                        />
                    </div>
                )}
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default ModuleFormModal;
